import React, {ReactElement, ReactNode, RefObject} from 'react'
import NatDisasterRepo from '../repository/NatDisasterRepo'
import {CommentInfo} from '../repository/NatGetDisasterCommentForAdmin'
import {Nullable} from '../../../../app/TypeHelpers'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {NatPostDisasterCommentFileUploadRequest} from '../repository/NatPostDisasterCommentFileUpload'
import {NatPostDisasterCommentForAdminRequest} from '../repository/NatPostDisasterCommentForAdmin'
import {NatPutDisasterCommentForAdminRequest,} from '../repository/NatPutDisasterCommentForAdmin'
import {Messages} from '../../../../Messages'
import {AsyncCallFunctionType} from '../../../../components/shared/hoc/withAsyncCommunication'
import {Moment} from 'moment'
import NatAdminDisasterCommentList from './NatAdminDisasterCommentList'
import {Box} from '@mui/material'
import {AdminPrimaryOutlinedButton} from '../../../../styledComponents/adminPage/Buttons'
import {CenterRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEdit} from '@fortawesome/free-regular-svg-icons'
import NatAdminDisasterCommentInputForm, {DisasterCommentFile} from './NatAdminDisasterCommentInputForm'
import {VIEW_DO_OR_NOT} from '../../../../CodeMaster'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import {InputError} from './NatAdminDisasterCommentError'
import Scroll from 'react-scroll'

interface Props {
    condominiumCd: string
    topicId: number
    CommentInfoList: CommentInfo[]
    currentDateTime: Moment
    pastFlag: boolean
    commentId?: number
    disasterRepo: NatDisasterRepo
    onPostSuccess: () => Promise<void>
    eventEmitter: EventEmitterExAdmin
    asyncCallFunction: AsyncCallFunctionType
}

interface State {
    isClickNewButton: boolean
    inputtingComment: string
    inputtingCommentFileList: DisasterCommentFile[]
    inputError: Nullable<InputError>
}

export class NatAdminDisasterCommentArea extends React.PureComponent<Props, State> {
    commentRef: RefObject<HTMLDivElement>

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            isClickNewButton: false,
            inputtingComment: '',
            inputtingCommentFileList: [],
            inputError: null,
        }

        this.commentRef = React.createRef()

        this.onClickSubmitButton = this.onClickSubmitButton.bind(this)
    }

    async createDisasterComment(request: NatPostDisasterCommentForAdminRequest): Promise<Nullable<number>> {
        const {disasterRepo, condominiumCd, topicId} = this.props

        const response =
            await disasterRepo.createDisasterCommentForAdmin(condominiumCd, topicId, request)
        if (response.requestError === null) {
            return response.details
        }
        return null
    }

    async updateDisasterComment(
        commentId: number,
        request: NatPutDisasterCommentForAdminRequest
    ): Promise<Nullable<number>> {
        const {disasterRepo, condominiumCd, topicId} = this.props

        const response =
            await disasterRepo.updateDisasterCommentForAdmin(condominiumCd, topicId, commentId, request)
        if (response.requestError === null) {
            return commentId
        }
        return null
    }

    createCommentWithFile = async (
        actionType: 'reply' | 'new' | 'edit',
        action: () => Promise<Nullable<number>>,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        await this.props.eventEmitter.showLoading()
        let inputError: Nullable<InputError> = null
        try {
            const commentId: Nullable<number> =
                await this.props.asyncCallFunction(
                    action,
                    async (httpErrorOrNetworkError) => {
                        const messages = ['入力内容に誤りがあります。']
                        if (httpErrorOrNetworkError.responseBody) {
                            httpErrorOrNetworkError.responseBody.requestItemErrors.forEach(item => {
                                messages.push(item.message)
                            })
                        }

                        inputError = {
                            title: Messages.EOPCR0001.toString('コメント'),
                            messages: messages
                        }
                    }
                )
            if (!commentId) {
                return inputError
            }
            if (fileRequests.length > 0) {
                inputError = await this.fileUpload(fileRequests, commentId)
                if (inputError !== null) {
                    this.setState({inputError: inputError})
                }
            }
            await this.props.onPostSuccess()
            if (actionType === 'new') {
                this.handleScroll(this.commentRef)
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return inputError
    }

    fileUpload = async (
        requests: NatPostDisasterCommentFileUploadRequest[],
        commentId: number
    ): Promise<Nullable<InputError>> => {
        const {disasterRepo, condominiumCd, topicId} = this.props

        let reportIndex: number = 1
        const fileCount: number = requests.length
        const errorFiles: string[] = []

        for (const request of requests) {
            this.props.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${reportIndex}/${fileCount}`)
            try {
                // AsyncCallFunctionによる標準の通信エラー処理を行わない
                // 原因はなんであれファイルを再登録する必要があることをユーザーに伝える必要があるため、全ての例外を捕捉する
                await disasterRepo.fileUploadDisasterComment(condominiumCd, topicId, commentId, request)
                reportIndex = reportIndex + 1
            } catch (e) {
                errorFiles.push(request.fileName)
            }
        }

        if (errorFiles.length > 0) {
            return {
                commentId: commentId,
                title: '画像のアップロードに失敗しました。',
                messages: [
                    'コメントを投稿できましたが、以下の画像のアップロード中に問題が発生しました。',
                    '画像を再度登録してください。',
                ],
                fileNameList: errorFiles
            }
        }
        return null
    }

    async onClickSubmitButton(): Promise<void> {
        const {inputtingComment, inputtingCommentFileList} = this.state

        const fileRequests: NatPostDisasterCommentFileUploadRequest[] =
            inputtingCommentFileList.map(file => {
                const data: string[] = String(file.fileData).split(',')
                return {fileName: file.fileName, fileData: data[1]}
            })
        const commentRequest: NatPostDisasterCommentForAdminRequest = {
            replySourceId: null,
            comment: inputtingComment,
            imageAttachedFlag: fileRequests.length === 0 ?
                VIEW_DO_OR_NOT.HIDDEN.value : VIEW_DO_OR_NOT.DISPLAY.value
        }

        const inputError = await this.handleCreateComment(commentRequest, fileRequests)
        if (inputError && !inputError.fileNameList) {
            this.setState({inputError: inputError})
        } else {
            this.setState({
                isClickNewButton: false,
                inputtingComment: '',
                inputtingCommentFileList: [],
                inputError: null
            })
        }
    }

    onClickCancelButton = (): void => {
        this.setState({
            isClickNewButton: false,
            inputtingComment: '',
            inputtingCommentFileList: [],
            inputError: null
        })
    }

    handleChangeInputComment = (
        comment: string,
        commentFileList: DisasterCommentFile[],
        deleteFileNumberList: number[],
        inputError: Nullable<InputError>
    ): void => {
        this.setState({
            inputtingComment: comment,
            inputtingCommentFileList: [...commentFileList],
            inputError: inputError
        })
    }

    handleCreateComment = async (
        commentRequest: NatPostDisasterCommentForAdminRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.createCommentWithFile(
            'new',
            () => this.createDisasterComment(commentRequest),
            fileRequests,
        )
    }

    handleReplyComment = async (
        commentRequest: NatPostDisasterCommentForAdminRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.createCommentWithFile(
            'reply',
            () => this.createDisasterComment(commentRequest),
            fileRequests,
        )
    }

    handleEditComment = async (
        commentId: number,
        commentRequest: NatPutDisasterCommentForAdminRequest,
        fileRequests: NatPostDisasterCommentFileUploadRequest[]
    ): Promise<Nullable<InputError>> => {
        return await this.createCommentWithFile(
            'edit',
            () => this.updateDisasterComment(commentId, commentRequest),
            fileRequests,
        )
    }

    handleDeleteComment = async (commentId: number): Promise<void> => {
        const {disasterRepo, condominiumCd, topicId} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                disasterRepo.deleteDisasterCommentForAdmin.bind(
                    disasterRepo, condominiumCd, topicId, commentId),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response && response.requestError === null) {
                await this.props.onPostSuccess()
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleClickNewCommentButton = (): void => {
        this.setState({isClickNewButton: true})
    }

    handleScroll = (element: RefObject<HTMLDivElement>): void => {
        if (element.current) {
            Scroll.animateScroll.scrollTo(element.current.offsetTop, {
                delay: 100,
                duration: 500,
                smooth: true
            })
        }
    }

    renderNewCommentInputForm(): ReactNode {
        const {inputtingComment, inputtingCommentFileList, inputError} = this.state
        return (
            <>
                <NatAdminDisasterCommentInputForm
                    type={`new-comment`}
                    submitButtonLabel={`投稿する`}
                    clearButtonLabel={'キャンセル'}
                    onClickSubmitButton={this.onClickSubmitButton}
                    onClickCancelButton={this.onClickCancelButton}
                    onChange={this.handleChangeInputComment}
                    inputtingComment={inputtingComment}
                    inputtingCommentFileList={inputtingCommentFileList}
                    deleteFileNumberList={[]}
                    inputError={!inputError?.fileNameList ? inputError : null}
                />
            </>
        )
    }

    render(): ReactElement {
        const {condominiumCd, topicId, currentDateTime, pastFlag, commentId, CommentInfoList, eventEmitter} = this.props
        const {isClickNewButton, inputError} = this.state
        return (
            <>
                <div ref={this.commentRef}>
                    <NatAdminDisasterCommentList
                        condominiumCd={condominiumCd}
                        topicId={topicId}
                        CommentInfoList={CommentInfoList}
                        currentDateTime={currentDateTime}
                        pastFlag={pastFlag}
                        onReply={this.handleReplyComment}
                        onEdit={this.handleEditComment}
                        onDelete={this.handleDeleteComment}
                        inputError={inputError}
                        eventEmitter={eventEmitter}
                    />
                </div>
                <>
                    {!pastFlag &&
                        !commentId &&
                        <AuthorityChecker
                            permittedAuthorities={HKConstants.CMN.AUTH.POST_COMMENT_DISASTER_INFORMATION_SHARING}>
                            <Box height={'16px'}/>
                            {isClickNewButton ? this.renderNewCommentInputForm() :
                                <AdminPrimaryOutlinedButton onClick={this.handleClickNewCommentButton}>
                                    <CenterRowFlexContainer>
                                        <FontAwesomeIcon icon={faEdit}/>
                                        <Box ml={'15px'}>新しいコメントを投稿する</Box>
                                    </CenterRowFlexContainer>
                                </AdminPrimaryOutlinedButton>
                            }
                        </AuthorityChecker>
                    }
                </>
            </>
        )
    }
}
